/**
 * Request browser notification permission. Returns true if granted.
 * No-ops silently if the Notification API is unavailable (e.g. iOS PWA).
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Display a browser notification if permission is already granted.
 * Focuses the window and closes the notification when clicked.
 * @param {string} title
 * @param {object} options - passed directly to new Notification()
 */
export const showNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  }
};

/**
 * Fire a status-change notification for a given order. Maps the order status
 * to a human-readable title/body and calls showNotification. No-op for unknown statuses.
 * @param {string} orderStatus - one of: confirmed | preparing | ready | out_for_delivery | delivered
 * @param {string} orderId
 */
export const sendOrderNotification = (orderStatus, orderId) => {
  const statusMessages = {
    confirmed: {
      title: '✅ Order Confirmed!',
      body: 'Your order is confirmed and being prepared.'
    },
    preparing: {
      title: '☕ Preparing Your Order',
      body: 'Our barista is brewing your perfect cup!'
    },
    ready: {
      title: '🎉 Order Ready!',
      body: 'Your order is ready for delivery.'
    },
    out_for_delivery: {
      title: '🚀 On the Way!',
      body: 'Your order is being delivered to your desk.'
    },
    delivered: {
      title: '✨ Order Delivered!',
      body: 'Your order has been delivered. Enjoy!'
    }
  };

  const message = statusMessages[orderStatus];
  if (message) {
    showNotification(message.title, {
      body: message.body,
      tag: `order-${orderId}`,
      requireInteraction: true,
      data: { orderId }
    });
  }
};
